// Maskeleme kaynağı (PDF / görsel) yükleme, sayfa çizimi, döndürme ve kalıcı (piksel) maskeli çıktı üretimi
import { pdfjs } from 'react-pdf';

if (!pdfjs.GlobalWorkerOptions.workerSrc) pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const MAX_PDF_PX = 2400;
const MAX_IMG_PX = 4000;
const PDF_SCALE = 150 / 72;
const tick = () => new Promise((r) => setTimeout(r, 0));

const makeCanvas = (w, h) => {
  const c = document.createElement('canvas');
  c.width = Math.round(w);
  c.height = Math.round(h);
  return c;
};

export const loadSource = async (file, ext) => {
  if (ext === 'pdf') {
    const pdf = await pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()), isEvalSupported: false }).promise;
    return { kind: 'pdf', pdf, numPages: pdf.numPages, cache: new Map() };
  }
  const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
  const k = Math.min(1, MAX_IMG_PX / Math.max(bmp.width, bmp.height));
  const c = makeCanvas(bmp.width * k, bmp.height * k);
  c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
  bmp.close?.();
  return { kind: 'image', base: c, numPages: 1, cache: new Map() };
};

const rotateCanvas = (src, rotation) => {
  if (!rotation) return src;
  const swap = rotation % 180 !== 0;
  const c = makeCanvas(swap ? src.height : src.width, swap ? src.width : src.height);
  const ctx = c.getContext('2d');
  ctx.translate(c.width / 2, c.height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.drawImage(src, -src.width / 2, -src.height / 2);
  return c;
};

// Sayfa görüntüsü (maskeleme koordinatları bu piksel uzayındadır; yakınlaştırmadan bağımsız)
export const getPageCanvas = async (src, index, rotation = 0) => {
  const key = `${index}:${rotation}`;
  if (src.cache.has(key)) return src.cache.get(key);
  let canvas;
  if (src.kind === 'pdf') {
    const page = await src.pdf.getPage(index + 1);
    const vp0 = page.getViewport({ scale: 1 });
    const scale = Math.min(PDF_SCALE, MAX_PDF_PX / Math.max(vp0.width, vp0.height));
    const vp = page.getViewport({ scale });
    canvas = makeCanvas(vp.width, vp.height);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport: vp }).promise;
    page.cleanup();
    if (src.cache.size > 4) src.cache.delete(src.cache.keys().next().value);
  } else {
    canvas = rotateCanvas(src.base, rotation);
  }
  src.cache.set(key, canvas);
  return canvas;
};

// Görsel 90° döndürülünce mevcut alanlar da aynı dönüşümle taşınır (W, H: döndürme öncesi boyut)
export const rotateRects = (rects, dir, W, H) => rects.map((r) => (dir > 0
  ? { ...r, x: H - (r.y + r.h), y: r.x, w: r.h, h: r.w }
  : { ...r, x: r.y, y: W - (r.x + r.w), w: r.h, h: r.w }));

const toBlob = (canvas, type) => new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Sayfa görüntüsü oluşturulamadı'))), type, 0.92));

// Siyah alanlar doğrudan piksellere işlenir; çıktı yalnızca yeni piksel verisi içerir (metin katmanı / metadata yok)
export const exportPages = async (src, rectsByPage, rotation, ext, onProgress) => {
  const blobs = [];
  for (let i = 0; i < src.numPages; i += 1) {
    const page = await getPageCanvas(src, i, src.kind === 'image' ? rotation : 0);
    const out = makeCanvas(page.width, page.height);
    const ctx = out.getContext('2d');
    ctx.drawImage(page, 0, 0);
    ctx.fillStyle = '#000';
    (rectsByPage[i] || []).forEach((r) => ctx.fillRect(Math.floor(r.x), Math.floor(r.y), Math.ceil(r.w) + 1, Math.ceil(r.h) + 1));
    blobs.push(await toBlob(out, ext === 'png' ? 'image/png' : 'image/jpeg'));
    out.width = 0;
    onProgress?.(i + 1, src.numPages);
    await tick();
  }
  return blobs;
};
